import React, {
  useEffect, useState, useMemo, useCallback, Fragment, ReactElement,
} from 'react';
import ActionButton from '../../../components/action-button/action-button.component';
import TableRowForm from '../../../components/table-row-form/table-row-form.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import FilteredCell from '../../../components/filtered-cell/filtered-cell.component';
import IconBlockError from '../../../components/icon-block-error/icon-block-error.component';
import IconBlockSuccess from '../../../components/icon-block-success/icon-block-success.component';
import FieldSeparatedBlock from '../../../components/field-separated-block/field-separated-block.component';
import Loader from '../../../components/loader/loader.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldMultiSelect from '../../../components/form-field-multi-select/form-field-multi-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import FormFieldCheckbox from '../../../components/form-field-checkbox/form-field-checkbox.component';
import FormFieldDate from '../../../components/form-field-date/form-field-date.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import StructureService from '../../../services/structure-service';
import FormService from '../../../services/form-service';
import useShiftState from '../../../hooks/use-shift-state';
import { CONTACT_TYPE_COMPANY, CONTACT_TYPE_PERSON } from '../../../constants/entity/contact-types';
import { TABLE_NAME_DEAL_LIST } from '../../../constants/system/table-names';
import { DEAL_STATUS_DRAFT } from '../../../constants/entity/deal-statuses';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';
import PersonDefaultImage from '../../../static/images/person-default-image.png';

const DealRowForm: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isActive,
    isSaving,
    isSaveDisabled,
    isInlineSaving,
    inputRefs,
    tableColumnSettings,
    currentUser,
    dealIndex,
    dealData,
    dealTypeList,
    dealStatusList,
    assetTypeList,
    contactPersonInternalList,
    contactCompanyList,
    contactPersonList,
    measureUnitList,
    assetOwnershipStructureTypeList,
    dealLoanTypeList,
    tagList,
    onInlineSave,
    onInlineCancel,
    onTagsChange,
    onAssetTypesChange,
    onAssetSellersChange,
    onSponsorsChange,
    onBrokersChange,
    onLendersChange,
    onSponsorContactsChange,
    onBrokerContactsChange,
    onLenderContactsChange,
    onNewDealActivityDealContactsChange,
    onContactPersonSearch,
    onContactPersonSearchClose,
    onContactPersonInternalSearch,
    onContactPersonInternalSearchClose,
    onContactCompanySearch,
    onContactCompanySearchClose,
    onTagSearch,
    onTagSearchClose,
  } = props;

  const isShiftPressed: boolean = useShiftState();

  const isDraft: boolean = dealData && dealData.status === DEAL_STATUS_DRAFT;

  const [selectedAssetSellers, setSelectedAssetSellers]: [Contact[] | null, Function] = useState(null);
  const [selectedAssetTypes, setSelectedAssetTypes]: [AssetType[] | null, Function] = useState(null);
  const [selectedTags, setSelectedTags]: [Tag | null, Function] = useState(null);
  const [selectedNewDealActivityDealContacts, setSelectedNewDealActivityDealContacts]: [Contact[] | null, Function]
    = useState(null);
  const [selectedSponsors, setSelectedSponsors]: [Contact[] | null, Function] = useState(null);
  const [selectedBrokers, setSelectedBrokers]: [Contact[] | null, Function] = useState(null);
  const [selectedLenders, setSelectedLenders]: [Contact[] | null, Function] = useState(null);
  const [selectedSponsorContacts, setSelectedSponsorContacts]: [Contact[] | null, Function] = useState(null);
  const [selectedBrokerContacts, setSelectedBrokerContacts]: [Contact[] | null, Function] = useState(null);
  const [selectedLenderContacts, setSelectedLenderContacts]: [Contact[] | null, Function] = useState(null);

  const filteredDealStatusList: DealStatus[] = useMemo((): DealStatus[] => (
    (dealStatusList && dealStatusList.length && (
      isDraft ? dealStatusList
        : dealStatusList.filter((dealStatusItem: DealStatus): boolean => dealStatusItem.name !== DEAL_STATUS_DRAFT)
    )) || []
  ), [dealStatusList, isDraft]);

  const dealId: number = dealData && dealData.id;
  const formKeyDeal: string = FormService.getIndexedFormFieldKey(dealId);
  const formPrefix: string = `dealListData[${formKeyDeal}]`;

  const menuItemFilteredDealStatusList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: filteredDealStatusList,
  }), [filteredDealStatusList]);

  const menuItemContactCompanySponsorList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactCompanyList,
    defaultImage: CompanyDefaultImage,
    selectedItemList: selectedSponsors,
  }), [contactCompanyList, selectedSponsors]);

  const menuItemContactCompanyBrokerList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactCompanyList,
    defaultImage: CompanyDefaultImage,
    selectedItemList: selectedBrokers,
  }), [contactCompanyList, selectedBrokers]);

  const menuItemContactCompanyLenderList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactCompanyList,
    defaultImage: CompanyDefaultImage,
    selectedItemList: selectedLenders,
  }), [contactCompanyList, selectedLenders]);

  const menuItemDealTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: dealTypeList,
  }), [dealTypeList]);

  const menuItemAssetTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: assetTypeList,
    selectedItemList: selectedAssetTypes,
  }), [assetTypeList, selectedAssetTypes]);

  const menuItemContactCompanyList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactCompanyList,
    defaultImage: CompanyDefaultImage,
  }), [contactCompanyList]);

  const menuItemContactCompanyAssetSellerList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactCompanyList,
    defaultImage: CompanyDefaultImage,
    selectedItemList: selectedAssetSellers,
  }), [contactCompanyList, selectedAssetSellers]);

  const menuItemMeasureUnitList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: measureUnitList,
  }), [measureUnitList]);

  const menuItemContactPersonActivityDealContactList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactPersonInternalList,
    selectedItemList: selectedNewDealActivityDealContacts,
    defaultImage: PersonDefaultImage,
  }), [contactPersonInternalList, selectedNewDealActivityDealContacts]);

  const menuItemAssetOwnershipStructureTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: assetOwnershipStructureTypeList,
  }), [assetOwnershipStructureTypeList]);

  const menuItemDealLoanTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: dealLoanTypeList,
  }), [dealLoanTypeList]);

  const menuItemTagList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionItemLabelKey: 'name',
    optionList: tagList,
    selectedItemList: selectedTags,
  }), [tagList, selectedTags]);

  const menuItemContactPersonSponsorList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactPersonList,
    defaultImage: PersonDefaultImage,
    selectedItemList: selectedSponsorContacts,
  }), [contactPersonList, selectedSponsorContacts]);

  const menuItemContactPersonBrokerList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactPersonList,
    defaultImage: PersonDefaultImage,
    selectedItemList: selectedBrokerContacts,
  }), [contactPersonList, selectedBrokerContacts]);

  const menuItemContactPersonLenderList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactPersonList,
    defaultImage: PersonDefaultImage,
    selectedItemList: selectedLenderContacts,
  }), [contactPersonList, selectedLenderContacts]);

  const assetSellersChange: Function = useCallback((contactValues: Contact[]): void => {
    if (onAssetSellersChange) {
      onAssetSellersChange(dealId, contactValues);
    }

    setSelectedAssetSellers(contactValues);
  }, [dealId]);

  const assetTypesChange: Function = useCallback((assetTypeValues: AssetType[]): void => {
    if (onAssetTypesChange) {
      onAssetTypesChange(dealId, assetTypeValues);
    }

    setSelectedAssetTypes(assetTypeValues);
  }, [dealId]);

  const tagsChange: Function = useCallback((tagValues: Tag[]): void => {
    if (onTagsChange) {
      onTagsChange(dealId, tagValues);
    }

    setSelectedTags(tagValues);
  }, [dealId]);

  const newDealActivityDealContactsChange: Function = useCallback((contactValues: Contact): void => {
    if (onNewDealActivityDealContactsChange) {
      onNewDealActivityDealContactsChange(dealId, contactValues);
    }

    setSelectedNewDealActivityDealContacts(contactValues);
  }, [dealId]);

  const sponsorsChange: Function = useCallback((contactValues: Contact): void => {
    if (onSponsorsChange) {
      onSponsorsChange(dealId, contactValues);
    }

    setSelectedSponsors(contactValues);
  }, [dealId]);

  const brokersChange: Function = useCallback((contactValues: Contact): void => {
    if (onBrokersChange) {
      onBrokersChange(dealId, contactValues);
    }

    setSelectedBrokers(contactValues);
  }, [dealId]);

  const lendersChange: Function = useCallback((contactValues: Contact): void => {
    if (onLendersChange) {
      onLendersChange(dealId, contactValues);
    }

    setSelectedLenders(contactValues);
  }, [dealId]);

  const sponsorContactsChange: Function = useCallback((contactValues: Contact): void => {
    if (onSponsorContactsChange) {
      onSponsorContactsChange(dealId, contactValues);
    }

    setSelectedSponsorContacts(contactValues);
  }, [dealId]);

  const brokerContactsChange: Function = useCallback((contactValues: Contact): void => {
    if (onBrokerContactsChange) {
      onBrokerContactsChange(dealId, contactValues);
    }

    setSelectedBrokerContacts(contactValues);
  }, [dealId]);

  const lenderContactsChange: Function = useCallback((contactValues: Contact): void => {
    if (onLenderContactsChange) {
      onLenderContactsChange(dealId, contactValues);
    }

    setSelectedLenderContacts(contactValues);
  }, [dealId]);

  const tableDealListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'name',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isSticky isStickyLeft>
          <FormFieldText
            isDisabled={isDraft}
            name={`${formPrefix}.name`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 2,
      name: 'asset.portfolio.name',
      component: (): ReactElement => (
        <TableCell isText isDisabled>
          {
            (dealData.asset && dealData.asset.portfolio && dealData.asset.portfolio.name)
            || (dealData.portfolio && dealData.portfolio.name)
          }
        </TableCell>
      ),
    },
    {
      id: 3,
      name: 'status',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = isDraft || (filteredDealStatusList && !filteredDealStatusList.length);

        return (
          <TableCell>
            <FormFieldSelect
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.status`}
              optionsTotal={filteredDealStatusList && filteredDealStatusList.length}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${dealIndex}.${fieldIndex}` : null}
            >
              {menuItemFilteredDealStatusList}
            </FormFieldSelect>
          </TableCell>
        );
      },
    },
    {
      id: 4,
      name: 'rejectReason',
      component: (): ReactElement => (
        <TableCell isText isDisabled>
          {dealData.rejectReason}
        </TableCell>
      ),
    },
    {
      id: 5,
      name: 'rejectReasonComment',
      component: (): ReactElement => (
        <TableCell isText isDisabled>
          {dealData.rejectReasonComment}
        </TableCell>
      ),
    },
    {
      id: 6,
      name: 'receivedDateValue',
      component: (): ReactElement => (
        <TableCell isText>
          <FormFieldDate name={`${formPrefix}.receivedDate`} />
        </TableCell>
      ),
    },
    {
      id: 7,
      name: 'receivedDateWeekOf',
      component: (): ReactElement => (
        <TableCell isText isDisabled>
          {dealData.receivedDateWeekOf}
        </TableCell>
      ),
    },
    {
      id: 8,
      name: 'closedDateValue',
      component: (): ReactElement => (
        <TableCell isText>
          <FormFieldDate name={`${formPrefix}.closedDate`} />
        </TableCell>
      ),
    },
    {
      id: 9,
      name: 'bidDateValue',
      component: (): ReactElement => (
        <TableCell isText>
          <FormFieldDate withBlankValues name={`${formPrefix}.bidDate`} />
        </TableCell>
      ),
    },
    {
      id: 10,
      name: 'sponsors.fullName',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = isDraft || (contactCompanyList && !contactCompanyList.length);

        return (
          <TableCell>
            <FormFieldMultiSelect
              withImage
              withSearch
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.sponsorIds`}
              optionList={contactCompanyList}
              defaultImage={CompanyDefaultImage}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${dealIndex}.${fieldIndex}` : null}
              onValuesChange={sponsorsChange}
              onSearch={onContactCompanySearch}
              onClose={onContactCompanySearchClose}
            >
              {menuItemContactCompanySponsorList}
            </FormFieldMultiSelect>
          </TableCell>
        );
      },
    },
    {
      id: 11,
      name: 'sponsorContacts.fullName',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = isDraft || (contactPersonList && !contactPersonList.length);

        return (
          <TableCell>
            <FormFieldMultiSelect
              withImage
              withSearch
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              nameKey="fullName"
              name={`${formPrefix}.sponsorContactIds`}
              optionList={contactPersonList}
              defaultImage={PersonDefaultImage}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${dealIndex}.${fieldIndex}` : null}
              onSearch={onContactPersonSearch}
              onClose={onContactPersonSearchClose}
              onValuesChange={sponsorContactsChange}
            >
              {menuItemContactPersonSponsorList}
            </FormFieldMultiSelect>
          </TableCell>
        );
      },
    },
    {
      id: 12,
      name: 'brokers.fullName',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = isDraft || (contactCompanyList && !contactCompanyList.length);

        return (
          <TableCell>
            <FormFieldMultiSelect
              withImage
              withSearch
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.brokerIds`}
              optionList={contactCompanyList}
              defaultImage={CompanyDefaultImage}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${dealIndex}.${fieldIndex}` : null}
              onSearch={onContactCompanySearch}
              onClose={onContactCompanySearchClose}
              onValuesChange={brokersChange}
            >
              {menuItemContactCompanyBrokerList}
            </FormFieldMultiSelect>
          </TableCell>
        );
      },
    },
    {
      id: 13,
      name: 'brokerContacts.fullName',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = isDraft || (contactPersonList && !contactPersonList.length);

        return (
          <TableCell>
            <FormFieldMultiSelect
              withImage
              withSearch
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              nameKey="fullName"
              name={`${formPrefix}.brokerContactIds`}
              defaultImage={PersonDefaultImage}
              optionList={contactPersonList}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${dealIndex}.${fieldIndex}` : null}
              onSearch={onContactPersonSearch}
              onClose={onContactPersonSearchClose}
              onValuesChange={brokerContactsChange}
            >
              {menuItemContactPersonBrokerList}
            </FormFieldMultiSelect>
          </TableCell>
        );
      },
    },
    {
      id: 14,
      name: 'lenders.fullName',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = isDraft || (contactCompanyList && !contactCompanyList.length);

        return (
          <TableCell>
            <FormFieldMultiSelect
              withImage
              withSearch
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.lenderIds`}
              optionList={contactCompanyList}
              defaultImage={CompanyDefaultImage}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${dealIndex}.${fieldIndex}` : null}
              onSearch={onContactCompanySearch}
              onClose={onContactCompanySearchClose}
              onValuesChange={lendersChange}
            >
              {menuItemContactCompanyLenderList}
            </FormFieldMultiSelect>
          </TableCell>
        );
      },
    },
    {
      id: 15,
      name: 'lenderContacts.fullName',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = isDraft || (contactPersonList && !contactPersonList.length);

        return (
          <TableCell>
            <FormFieldMultiSelect
              withImage
              withSearch
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              nameKey="fullName"
              name={`${formPrefix}.lenderContactIds`}
              defaultImage={PersonDefaultImage}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${dealIndex}.${fieldIndex}` : null}
              optionList={contactPersonList}
              onSearch={onContactPersonSearch}
              onClose={onContactPersonSearchClose}
              onValuesChange={lenderContactsChange}
            >
              {menuItemContactPersonLenderList}
            </FormFieldMultiSelect>
          </TableCell>
        );
      },
    },
    {
      id: 16,
      name: 'amount',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            name={`${formPrefix}.amount`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 17,
      name: 'loanAmount',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            isDisabled={isDraft}
            name={`${formPrefix}.loanAmount`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 18,
      name: 'typeText',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = isDraft || (dealTypeList && !dealTypeList.length);

        return (
          <TableCell>
            <FormFieldSelect
              withBlankElement
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.type`}
              optionsTotal={dealTypeList && dealTypeList.length}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${dealIndex}.${fieldIndex}` : null}
            >
              {menuItemDealTypeList}
            </FormFieldSelect>
          </TableCell>
        );
      },
    },
    {
      id: 19,
      name: 'asset.city',
      component: (): ReactElement => (
        <TableCell isText isDisabled>
          {dealData.asset && dealData.asset.city}
        </TableCell>
      ),
    },
    {
      id: 20,
      name: 'asset.state',
      component: (): ReactElement => (
        <TableCell isText isDisabled>
          {dealData.asset && dealData.asset.state}
        </TableCell>
      ),
    },
    {
      id: 21,
      name: 'asset.msaLocation',
      component: (): ReactElement => (
        <TableCell isText isDisabled>
          {dealData.asset && dealData.asset.msaLocation}
        </TableCell>
      ),
    },
    {
      id: 22,
      name: 'asset.submarket',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            isDisabled={isDraft}
            name={`${formPrefix}.asset.submarket`}
            inputRefs={inputRefs}
            inputRefIndex={!isDraft ? `${dealIndex}.${fieldIndex}` : null}
          />
        </TableCell>
      ),
    },
    {
      id: 23,
      name: 'asset.typesText',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = isDraft || (assetTypeList && !assetTypeList.length);

        return (
          <TableCell>
            <FormFieldMultiSelect
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              keyField="name"
              name={`${formPrefix}.asset.types`}
              optionList={assetTypeList}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${dealIndex}.${fieldIndex}` : null}
              onValuesChange={assetTypesChange}
            >
              {menuItemAssetTypeList}
            </FormFieldMultiSelect>
          </TableCell>
        );
      },
    },
    {
      id: 24,
      name: 'newDealActivity.dealSource.name',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = isDraft || (contactCompanyList && !contactCompanyList.length);

        return (
          <TableCell>
            <FormFieldSelect
              withSearch
              withBlankElement
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.newDealActivity.dealSourceId`}
              optionsTotal={contactCompanyList && contactCompanyList.length}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${dealIndex}.${fieldIndex}` : null}
              onSearch={onContactCompanySearch}
              onClose={onContactCompanySearchClose}
            >
              {menuItemContactCompanyList}
            </FormFieldSelect>
          </TableCell>
        );
      },
    },
    {
      id: 25,
      name: 'newDealActivity.pricing',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            isDisabled={isDraft}
            name={`${formPrefix}.newDealActivity.pricing`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 26,
      name: 'newDealActivity.size',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = isDraft || (measureUnitList && !measureUnitList.length);
        const subFieldIndexSize: number = 1;
        const subFieldIndexSizeType: number = 2;

        return (
          <TableCell>
            <FieldSeparatedBlock>
              <FormFieldNumber
                isDisabled={isDisabled}
                name={`${formPrefix}.newDealActivity.size`}
                inputRefs={inputRefs}
                inputRefIndex={!isDisabled ? `${dealIndex}.${fieldIndex}.${subFieldIndexSize}` : null}
              />
              <FormFieldSelect
                withBlankElement
                isDisabled={isDisabled}
                isReadOnly={isShiftPressed}
                name={`${formPrefix}.newDealActivity.sizeType`}
                optionsTotal={measureUnitList && measureUnitList.length}
                inputRefs={inputRefs}
                inputRefIndex={!isDisabled ? `${dealIndex}.${fieldIndex}.${subFieldIndexSizeType}` : null}
              >
                {menuItemMeasureUnitList}
              </FormFieldSelect>
            </FieldSeparatedBlock>
          </TableCell>
        );
      },
    },
    {
      id: 27,
      name: 'newDealActivity.dealContacts.fullName',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = isDraft || (contactPersonInternalList && !contactPersonInternalList.length);

        return (
          <TableCell>
            <FormFieldMultiSelect
              withImage
              withSearch
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              nameKey="fullName"
              name={`${formPrefix}.newDealActivity.dealContactIds`}
              optionList={contactPersonInternalList}
              defaultImage={PersonDefaultImage}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${dealIndex}.${fieldIndex}` : null}
              onSearch={onContactPersonInternalSearch}
              onClose={onContactPersonInternalSearchClose}
              onValuesChange={newDealActivityDealContactsChange}
            >
              {menuItemContactPersonActivityDealContactList}
            </FormFieldMultiSelect>
          </TableCell>
        );
      },
    },
    {
      id: 28,
      name: 'newDealActivity.updatedDate',
      component: (): ReactElement => (
        <TableCell />
      ),
    },
    {
      id: 29,
      name: 'newDealActivity.lastComment',
      component: (): ReactElement => (
        <TableCell />
      ),
    },
    {
      id: 30,
      name: 'newDealActivity.lastAttachment',
      component: (): ReactElement => (
        <TableCell />
      ),
    },
    {
      id: 31,
      name: 'dealStructure',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            isDisabled={isDraft}
            name={`${formPrefix}.dealStructure`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 32,
      name: 'description',
      component: (): ReactElement => (
        <TableCell isText isDisabled>
          {dealData.description}
        </TableCell>
      ),
    },
    {
      id: 33,
      name: 'rfp',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isTextCenter>
          <FormFieldCheckbox
            isDisabled={isDraft}
            name={`${formPrefix}.rfp`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 34,
      name: 'askingPrice',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            isDisabled={isDraft}
            name={`${formPrefix}.askingPrice`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 35,
      name: 'equityRequired',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            isDisabled={isDraft}
            name={`${formPrefix}.equityRequired`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 36,
      name: 'estimatedTdc',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            isDisabled={isDraft}
            name={`${formPrefix}.estimatedTdc`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 37,
      name: 'asset.sellers.fullName',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = isDraft || (contactCompanyList && !contactCompanyList.length);

        return (
          <TableCell>
            <FormFieldMultiSelect
              withImage
              withSearch
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${dealIndex}.${fieldIndex}` : null}
              name={`${formPrefix}.asset.sellerIds`}
              optionList={contactCompanyList}
              defaultImage={CompanyDefaultImage}
              onSearch={onContactCompanySearch}
              onClose={onContactCompanySearchClose}
              onValuesChange={assetSellersChange}
            >
              {menuItemContactCompanyAssetSellerList}
            </FormFieldMultiSelect>
          </TableCell>
        );
      },
    },
    {
      id: 38,
      name: 'asset.assetOwnershipStructure',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldSelect
            withBlankElement
            isDisabled={isDraft}
            isReadOnly={isShiftPressed}
            name={`${formPrefix}.asset.assetOwnershipStructure`}
            optionsTotal={assetOwnershipStructureTypeList && assetOwnershipStructureTypeList.length}
            inputRefs={inputRefs}
            inputRefIndex={`${dealIndex}.${fieldIndex}`}
          >
            {menuItemAssetOwnershipStructureTypeList}
          </FormFieldSelect>
        </TableCell>
      ),
    },
    {
      id: 39,
      name: 'asset.affordableUnits',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            isDisabled={isDraft}
            name={`${formPrefix}.asset.affordableUnits`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 40,
      name: 'asset.asOfRightZoning',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isTextCenter>
          <FormFieldCheckbox
            isDisabled={isDraft}
            name={`${formPrefix}.asset.asOfRightZoning`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 41,
      name: 'loanType',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldSelect
            withBlankElement
            isDisabled={isDraft}
            isReadOnly={isShiftPressed}
            name={`${formPrefix}.loanType`}
            optionsTotal={dealLoanTypeList && dealLoanTypeList.length}
            inputRefs={inputRefs}
            inputRefIndex={`${dealIndex}.${fieldIndex}`}
          >
            {menuItemDealLoanTypeList}
          </FormFieldSelect>
        </TableCell>
      ),
    },
    {
      id: 42,
      name: 'extensionConditions',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            isDisabled={isDraft}
            name={`${formPrefix}.extensionConditions`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 43,
      name: 'equityAmount',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            isDisabled={isDraft}
            name={`${formPrefix}.equityAmount`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 44,
      name: 'totalFunded',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            isDisabled={isDraft}
            name={`${formPrefix}.totalFunded`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 45,
      name: 'totalUnfunded',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            isDisabled={isDraft}
            name={`${formPrefix}.totalUnfunded`}
            inputRefs={inputRefs}
            inputRefIndex={`${dealIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 46,
      name: 'zfaAmount',
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealData.zfaAmount}
        </TableCell>
      ),
    },
    {
      id: 47,
      name: 'gsfAmount',
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealData.gsfAmount}
        </TableCell>
      ),
    },
    {
      id: 48,
      name: 'nsfAmount',
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealData.nsfAmount}
        </TableCell>
      ),
    },
    {
      id: 49,
      name: 'unitsAmount',
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealData.unitsAmount}
        </TableCell>
      ),
    },
    {
      id: 50,
      name: 'keysAmount',
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealData.keysAmount}
        </TableCell>
      ),
    },
    {
      id: 51,
      name: 'acresAmount',
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealData.acresAmount}
        </TableCell>
      ),
    },
    {
      id: 52,
      name: 'lotsfAmount',
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealData.lotsfAmount}
        </TableCell>
      ),
    },
    {
      id: 53,
      name: 'capitalizationSummaryData.sourcesSummary.amount',
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealData.capitalizationSummaryData && dealData.capitalizationSummaryData.sourcesSummary
            && dealData.capitalizationSummaryData.sourcesSummary.amount}
        </TableCell>
      ),
    },
    {
      id: 54,
      name: 'capitalizationSummaryData.usesSummary.amount',
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealData.capitalizationSummaryData && dealData.capitalizationSummaryData.usesSummary
            && dealData.capitalizationSummaryData.usesSummary.amount}
        </TableCell>
      ),
    },
    {
      id: 55,
      name: 'capitalizationSummaryData.sources.debt.amount',
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealData.capitalizationSummaryData && dealData.capitalizationSummaryData.sources
            && dealData.capitalizationSummaryData.sources.debt
            && dealData.capitalizationSummaryData.sources.debt.amount}
        </TableCell>
      ),
    },
    {
      id: 56,
      name: 'capitalizationSummaryData.sources.debt.sharePercent',
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealData.capitalizationSummaryData && dealData.capitalizationSummaryData.sources
            && dealData.capitalizationSummaryData.sources.debt
            && dealData.capitalizationSummaryData.sources.debt.sharePercent}
        </TableCell>
      ),
    },
    {
      id: 57,
      name: 'capitalizationSummaryData.sources.equity.amount',
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealData.capitalizationSummaryData && dealData.capitalizationSummaryData.sources
            && dealData.capitalizationSummaryData.sources.equity
            && dealData.capitalizationSummaryData.sources.equity.amount}
        </TableCell>
      ),
    },
    {
      id: 58,
      name: 'capitalizationSummaryData.sources.equity.sharePercent',
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealData.capitalizationSummaryData && dealData.capitalizationSummaryData.sources
            && dealData.capitalizationSummaryData.sources.equity
            && dealData.capitalizationSummaryData.sources.equity.sharePercent}
        </TableCell>
      ),
    },
    {
      id: 59,
      name: 'capitalizationSummaryData.uses.costs.amount',
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealData.capitalizationSummaryData && dealData.capitalizationSummaryData.uses
            && dealData.capitalizationSummaryData.uses.costs
            && dealData.capitalizationSummaryData.uses.costs.amount}
        </TableCell>
      ),
    },
    {
      id: 60,
      name: 'capitalizationSummaryData.uses.costs.sharePercent',
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {dealData.capitalizationSummaryData && dealData.capitalizationSummaryData.uses
            && dealData.capitalizationSummaryData.uses.costs
            && dealData.capitalizationSummaryData.uses.costs.sharePercent}
        </TableCell>
      ),
    },
    {
      id: 61,
      name: 'newDealActivity.tags',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = isDraft || (tagList && !tagList.length);

        return (
          <TableCell>
            <FormFieldMultiSelect
              isAbleToAdd
              withSearch
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              keyField="name"
              name={`${formPrefix}.newDealActivity.tags`}
              optionList={tagList}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${dealIndex}.${fieldIndex}` : null}
              onSearch={onTagSearch}
              onClose={onTagSearchClose}
              onValuesChange={tagsChange}
            >
              {menuItemTagList}
            </FormFieldMultiSelect>
          </TableCell>
        );
      },
    },
    {
      id: 62,
      name: 'actions',
      component: (): ReactElement => (
        <TableCell isActions isTextRight isSticky isStickyRight>
          {
            isSaving && isInlineSaving && (
              <Loader isInline isSmall />
            )
          }
          {
            !isSaveDisabled && !isSaving && (
              <ActionButton
                withSmallPadding
                onClick={(): void => {
                  onInlineSave(dealId);
                }}
              >
                <IconBlockSuccess />
              </ActionButton>
            )
          }
          {
            !isSaving && (
              <ActionButton
                withSmallPadding
                onClick={(): void => {
                  onInlineCancel(dealId);
                }}
              >
                <IconBlockError isDisabled={isSaveDisabled} />
              </ActionButton>
            )
          }
        </TableCell>
      ),
    },
  ], [
    isShiftPressed, isDraft, selectedSponsors, selectedBrokers, selectedLenders, isSaveDisabled, contactPersonList,
    selectedNewDealActivityDealContacts, selectedTags, selectedAssetTypes, selectedAssetSellers, isSaving, dealData,
    selectedSponsorContacts, selectedBrokerContacts, selectedLenderContacts, contactPersonInternalList,
    contactCompanyList, contactPersonList, tagList,
  ]);

  useEffect((): void => {
    if (dealData) {
      const sponsorCompanyList: Contact[] = dealData.sponsors && dealData.sponsors.length
        ? dealData.sponsors.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_COMPANY) : [];
      const sponsorPersonList: Contact[] = dealData.sponsors && dealData.sponsors.length
        ? dealData.sponsors.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_PERSON) : [];
      const brokerCompanyList: Contact[] = dealData.brokers && dealData.brokers.length
        ? dealData.brokers.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_COMPANY) : [];
      const brokerPersonList: Contact[] = dealData.brokers && dealData.brokers.length
        ? dealData.brokers.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_PERSON) : [];
      const lenderCompanyList: Contact[] = dealData.lenders && dealData.lenders.length
        ? dealData.lenders.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_COMPANY) : [];
      const lenderPersonList: Contact[] = dealData.lenders && dealData.lenders.length
        ? dealData.lenders.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_PERSON) : [];

      if (!selectedAssetTypes) {
        setSelectedAssetTypes((dealData.asset && dealData.asset.types) || []);
      }

      if (!selectedTags) {
        setSelectedTags((dealData.newDealActivity && dealData.newDealActivity.tags) || []);
      }

      if (!selectedNewDealActivityDealContacts) {
        setSelectedNewDealActivityDealContacts(
          dealData.newDealActivity && dealData.newDealActivity.dealContacts
          && dealData.newDealActivity.dealContacts.length ? dealData.newDealActivity.dealContacts : [],
        );
      }

      if (!selectedSponsors) {
        setSelectedSponsors(sponsorCompanyList && sponsorCompanyList.length ? sponsorCompanyList : []);
      }

      if (!selectedBrokers) {
        setSelectedBrokers(brokerCompanyList && brokerCompanyList.length ? brokerCompanyList : []);
      }

      if (!selectedLenders) {
        setSelectedLenders(lenderCompanyList && lenderCompanyList.length ? lenderCompanyList : []);
      }

      if (!selectedSponsorContacts) {
        setSelectedSponsorContacts(sponsorPersonList && sponsorPersonList.length ? sponsorPersonList : []);
      }

      if (!selectedBrokerContacts) {
        setSelectedBrokerContacts(brokerPersonList && brokerPersonList.length ? brokerPersonList : []);
      }

      if (!selectedLenderContacts) {
        setSelectedLenderContacts(lenderPersonList && lenderPersonList.length ? lenderPersonList : []);
      }
    }
  }, [dealData]);

  return (
    <TableRowForm isActive={isActive}>
      {
        tableDealListFields && !!tableDealListFields.length
        && StructureService.extendOptions(tableDealListFields, tableColumnSettings, true)
          .map((tableField: TableField, fieldIndex: number): ReactElement | boolean => (
            !!tableField.component && (
              !tableField.isNotFiltered ? (
                <FilteredCell
                  key={tableField.id}
                  tableName={TABLE_NAME_DEAL_LIST}
                  columnName={tableField.name}
                  currentUser={currentUser}
                >
                  {tableField.component(tableField, fieldIndex)}
                </FilteredCell>
              ) : (
                <Fragment key={tableField.id}>
                  {tableField.component(tableField, fieldIndex)}
                </Fragment>
              )
            )
          ))
      }
    </TableRowForm>
  );
});

export default DealRowForm;
